import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { StudioProfileForm } from "@/components/StudioProfileForm";
import { Building2, Loader2, Clock, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center text-muted-foreground">
    <Loader2 className="h-5 w-5 animate-spin mr-2" />Caricamento…
  </div>
);

// Scompone i secondi residui in giorni/ore/minuti/secondi.
const breakdown = (secs) => {
  const s = Math.max(0, Math.floor(secs));
  return {
    d: Math.floor(s / 86400),
    h: Math.floor((s % 86400) / 3600),
    m: Math.floor((s % 3600) / 60),
    s: s % 60,
  };
};

const Segment = ({ value, label }) => (
  <div className="flex flex-col items-center min-w-[38px]">
    <span className="font-head font-extrabold text-lg leading-none tabular-nums">{String(value).padStart(2, "0")}</span>
    <span className="text-[9px] uppercase tracking-wider opacity-70">{label}</span>
  </div>
);

const TrialBanner = ({ endIso }) => {
  const navigate = useNavigate();
  const [left, setLeft] = useState(() => (new Date(endIso).getTime() - Date.now()) / 1000);
  useEffect(() => {
    const iv = setInterval(() => setLeft((new Date(endIso).getTime() - Date.now()) / 1000), 1000);
    return () => clearInterval(iv);
  }, [endIso]);
  const b = breakdown(left);
  return (
    <div
      data-testid="trial-countdown-banner"
      className="sticky top-14 md:top-0 z-30 bg-[#0A0A0A] text-white border-b border-white/10"
    >
      <div className="px-4 sm:px-8 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
        <div className="flex items-center gap-2 min-w-0">
          <Clock className="h-4 w-4 text-[#3B9EFF] shrink-0" />
          <p className="text-[12px] sm:text-[13px] leading-snug">
            <span className="font-semibold">Prova gratuita in corso.</span>{" "}
            Hai tempo fino alla scadenza per registrare il tuo Studio (ragione sociale, P.IVA, indirizzo…).
            Allo scadere, senza i dati dello Studio, le funzioni verranno bloccate.
          </p>
        </div>
        <div className="flex items-center gap-3 sm:ml-auto shrink-0">
          <div className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-1.5" data-testid="trial-countdown-timer">
            <Segment value={b.d} label="gg" />
            <span className="opacity-40">:</span>
            <Segment value={b.h} label="ore" />
            <span className="opacity-40">:</span>
            <Segment value={b.m} label="min" />
            <span className="opacity-40">:</span>
            <Segment value={b.s} label="sec" />
          </div>
          <Button
            data-testid="trial-complete-studio-btn"
            onClick={() => navigate("/profilo")}
            className="h-8 bg-[#007BFF] hover:bg-[#0069D9] text-white rounded-md text-xs whitespace-nowrap"
          >
            Completa lo Studio
          </Button>
        </div>
      </div>
    </div>
  );
};

const BlockedScreen = ({ onComplete }) => (
  <div className="min-h-screen bg-white" data-testid="trial-blocked-gate">
    <div className="max-w-2xl mx-auto px-4 py-10 sm:py-14">
      <div className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 mb-5" data-testid="trial-expired-notice">
        <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
        <div className="text-[13px] leading-relaxed text-amber-900">
          <span className="font-semibold">La prova gratuita è terminata.</span> Per continuare a usare Sedio devi
          registrare i dati del tuo Studio. Una volta completati, tutte le funzioni verranno sbloccate
          immediatamente e in modo definitivo.
        </div>
      </div>
      <div className="bg-white border border-border rounded-xl shadow-sm p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-11 w-11 rounded-lg bg-[#007BFF] flex items-center justify-center shrink-0">
            <Building2 className="h-6 w-6 text-white" strokeWidth={2.2} />
          </div>
          <div>
            <h1 className="font-head text-2xl font-extrabold tracking-tight">Registra il tuo Studio</h1>
            <p className="text-sm text-muted-foreground">Compila i dati per sbloccare tutte le funzioni.</p>
          </div>
        </div>
        <StudioProfileForm onSaved={(complete) => { if (complete) onComplete(); }} />
      </div>
    </div>
  </div>
);

export const TrialGate = ({ children }) => {
  const [state, setState] = useState({ loading: true, info: null });
  const location = useLocation();
  const lastPath = useRef(location.pathname);

  const load = useCallback(() => {
    api.get("/trial/status")
      .then((r) => setState({ loading: false, info: r.data }))
      // fail-open: non blocchiamo l'app su errori transitori
      .catch(() => setState({ loading: false, info: { exempt: true, blocked: false, show_timer: false } }));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Rivaluta lo stato quando si torna sul profilo (es. dopo aver salvato) o cambia rotta rilevante.
  useEffect(() => {
    if (lastPath.current !== location.pathname) {
      lastPath.current = location.pathname;
      if (state.info && state.info.show_timer) load();
    }
  }, [location.pathname, load, state.info]);

  if (state.loading) return <Loading />;
  const info = state.info || {};

  if (info.blocked) return <BlockedScreen onComplete={load} />;

  return (
    <>
      {info.show_timer && info.trial_end && <TrialBanner endIso={info.trial_end} />}
      {children}
    </>
  );
};
